import {
  ApiErrorCode,
  ApiErrorParams,
  ApiValidationIssue,
  HttpFallbackCode,
  httpFallbackCode,
  isApiErrorCode,
} from '@seiri/shared/common'

const noop = () => {
  // No operation function
}

export const LS_TOKEN = 'seiri_token'

/** Header used to tie a browser request to the backend log lines and Sentry events it produced. */
export const REQUEST_ID_HEADER = 'x-request-id'

/**
 * A failed API call, as an actual Error.
 *
 * `statusCode` 0 means no response arrived at all - the connection dropped, was refused, or the
 * page was torn down mid-request. The browser's own `TypeError: Failed to fetch` is kept as
 * `cause`; wrapping it is what gives a network failure the request id it was sent with, so the
 * backend logs can say whether it ever arrived. `code` is then `network.failed`.
 *
 * The previous code threw the parsed JSON body - a plain object. That meant `instanceof Error`
 * was false (so callers doing `error instanceof Error ? error.message : String(error)` rendered
 * "[object Object]") and Sentry captured it as a non-error with no stack and nothing to group on.
 *
 * `statusCode` is kept under that exact name because existing callers branch on it.
 */
export class ApiError extends Error {
  readonly statusCode: number
  readonly reqId: string | null
  readonly body: unknown

  constructor(statusCode: number, reqId: string | null, body: unknown, cause?: unknown) {
    super(ApiError.messageFrom(body, statusCode, cause), cause === undefined ? undefined : { cause })
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.reqId = reqId
    this.body = body
  }

  /**
   * What went wrong, as a translation key: the body's code from `API_ERRORS`, or - for a response
   * without one (a plain Nest exception, a proxy error page) - the fallback for the status.
   * `message` stays the backend's English, for Sentry and the console; `describeApiError` turns
   * this into what the user reads. See docs/api-errors.md.
   */
  get code(): ApiErrorCode | HttpFallbackCode {
    const code = (this.body as { code?: unknown } | null)?.code
    return isApiErrorCode(code) ? code : httpFallbackCode(this.statusCode)
  }

  /** No response arrived: a network failure, not something the API decided. */
  get isNetworkFailure(): boolean {
    return this.statusCode === 0
  }

  /**
   * A 401 with no code of its own: the session is missing or has expired. `HANDLE_AUTH_ERROR` signs
   * the user out on it, so there is nothing left to fix. A 401 that states a rule
   * (`auth.invalidCredentials`) is a refusal instead.
   */
  get isUnauthenticated(): boolean {
    return this.code === 'http.unauthorized'
  }

  /**
   * The API turned the request down on a rule it states: the body carries a code from `API_ERRORS`
   * (already registered, event full, plan limit). That is an outcome the user is told about, not a
   * defect, so it is not reported to Sentry as an error.
   *
   * `validation.failed` is not one. The forms validate against the same schemas before sending, so
   * a body the API still rejects means the frontend let through something it should not have.
   */
  get isRefusal(): boolean {
    const code = this.code
    return isApiErrorCode(code) && code !== 'validation.failed'
  }

  /** Narrows the error to one of `codes`, typing its `params`. */
  is<C extends ApiErrorCode>(...codes: C[]): this is ApiError & { readonly params: ApiErrorParams<C> } {
    return (codes as ApiErrorCode[]).includes(this.code as ApiErrorCode)
  }

  /** The code's params; narrow with `is(...)` to type them. */
  get params(): unknown {
    return (this.body as { params?: unknown } | null)?.params
  }

  /** The failed rules of a `validation.failed`, empty for anything else. */
  get issues(): ApiValidationIssue[] {
    const issues = (this.body as { issues?: unknown } | null)?.issues
    return Array.isArray(issues) ? issues : []
  }

  private static messageFrom(body: unknown, statusCode: number, cause: unknown): string {
    if (statusCode === 0) return cause instanceof Error ? cause.message : 'Network request failed'
    const message = (body as { message?: unknown } | null)?.message
    // Nest returns an array of strings for validation failures and a plain string otherwise.
    if (Array.isArray(message)) return message.join(', ')
    if (typeof message === 'string' && message) return message
    return `Request failed with status ${statusCode}`
  }
}

/** crypto.randomUUID needs a secure context; fall back so plain-http origins still correlate. */
export const newRequestId = (): string => {
  try {
    return crypto.randomUUID()
  } catch {
    return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

export const defaultGetTokens = () => {
  const token = window.localStorage.getItem(LS_TOKEN)
  return token || ''
}

export const fetchJson = async <T = unknown>(
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
  auth = false,
  getTokens = defaultGetTokens,
) => {
  const headers = new Headers(init?.headers)
  headers.append('Accept', 'application/json')
  if (auth) {
    const token = getTokens()
    if (token) {
      headers.append('Authorization', `Bearer ${token}`)
    }
  }
  detectJSONbodyAndAddHeaders(init, headers)
  // Sent on every call so a browser error, a Sentry event and the backend log lines for the same
  // operation can be joined on one id. The backend honours it and echoes it back.
  if (!headers.has(REQUEST_ID_HEADER)) {
    headers.set(REQUEST_ID_HEADER, newRequestId())
  }
  let response: Response
  try {
    response = await fetch(input, { ...init, headers })
  } catch (error) {
    // An abort is the caller's own decision and callers check for it; pass it through untouched.
    if (init?.signal?.aborted) throw error
    throw new ApiError(0, headers.get(REQUEST_ID_HEADER), null, error)
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    // The echo is authoritative, but a response the API never produced - a proxy's 502/504 - has
    // none. The id we sent still says which request it was, and that it never reached the logs.
    const reqId = response.headers.get(REQUEST_ID_HEADER) ?? headers.get(REQUEST_ID_HEADER)
    throw new ApiError(response.status, reqId, body)
  }
  return (await response.json()) as T
}

export const detectJSONbodyAndAddHeaders = (init: RequestInit | undefined, headers: Headers) => {
  if (init?.body && typeof init.body === 'string' && !headers.has('Content-Type')) {
    try {
      JSON.parse(init.body)
      headers.append('Content-Type', 'application/json')
    } catch {
      noop()
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- lint-debt
export const toQueryParams = <T extends Record<string, any>>(query: T, params = new URLSearchParams()): string => {
  if (!query) {
    return params.toString()
  }
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue

    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item))
      }
    } else {
      params.set(key, String(value))
    }
  }
  return params.toString()
}
